import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import { mmsApi } from "@/api/mmsApi";
const VideoSurveillance = ({ className = "", }) => {
    // 状态管理
    const [currentTime, setCurrentTime] = useState("");
    const [activeMonitor, setActiveMonitor] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const containerRef = useRef(null);
    const [camerasData, setCamerasData] = useState([]);
    useEffect(() => {
        getData();
    }, []);
    /**
     * @description: getData - 获取数据
     */
    const getData = async () => {
        try {
            const res = await mmsApi.getCameras();
            setCamerasData(res.data);
        }
        catch (error) {
            console.error("获取摄像头数据错误:", error);
        }
    };
    // 颜色映射
    const colorMap = {
        cyan: "#00FFFF", // 青色
        blue: "#1E90FF", // 蓝色
        green: "#32CD32", // 绿色
        online: "#32CD32", // 在线状态
        offline: "#FF6347", // 离线状态
        recording: "#FFD700", // 录制中状态
    };
    // 实时更新时间
    useEffect(() => {
        const updateTime = () => {
            const now = new Date();
            setCurrentTime(now
                .toLocaleString("zh-CN", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            })
                .replace(",", ""));
        };
        updateTime();
        const timer = setInterval(updateTime, 1000);
        return () => clearInterval(timer);
    }, []);
    // 处理全屏切换
    const toggleFullscreen = (e, deviceCode) => {
        e.stopPropagation();
        // 如果已经是全屏，则退出
        if (isFullscreen && activeMonitor === deviceCode) {
            setIsFullscreen(false);
            setActiveMonitor(null);
            return;
        }
        // 进入全屏
        setActiveMonitor(deviceCode);
        setIsFullscreen(true);
        // 强制重绘以避免位置偏移
        setTimeout(() => {
            window.dispatchEvent(new Event("resize"));
        }, 0);
    };
    return (_jsxs("div", { ref: containerRef, className: `
        relative 
        bg-gradient-to-tr from-[#071F4A] to-[#0f295300] 
        backdrop-blur-[0.14rem] 
        p-[0.05rem] 
        ${isFullscreen
            ? "fixed inset-0 z-50 m-0 p-0"
            : "grid grid-cols-3 grid-rows-2 gap-[0.2rem]"} 
        ${className}
        transition-all duration-300 ease-in-out
      `, children: [camerasData.map(({ deviceCode, deviceShortName, color = "blue", status = "online", url, }, index) => {
                const isActive = activeMonitor === deviceCode;
                const borderStyle = index === 5 ? "dashed" : "solid";
                // 全屏模式下只显示选中的监控项
                if (isFullscreen && !isActive)
                    return null;
                return (_jsxs("div", { className: `
    relative 
    border-[0.02rem] 
    ${borderStyle} 
    flex flex-col 
    bg-[#0B244B]
    overflow-hidden
    cursor-pointer
    transition-all duration-200 ease-in-out
    ${isActive
                        ? isFullscreen
                            ? "w-full h-full"
                            : "ring-2 ring-white/50 scale-[1.02] z-10"
                        : ""}
  `, style: {
                        borderColor: colorMap[color],
                        ...(isFullscreen && isActive
                            ? {
                                position: "fixed",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                margin: 0,
                                zIndex: 60,
                                borderRadius: 0,
                            }
                            : {}),
                    }, onClick: () => !isFullscreen &&
                        setActiveMonitor(deviceCode === activeMonitor ? null : deviceCode), children: [_jsx("div", { className: "absolute z-10 pointer-events-none", style: {
                                top: 0,
                                left: 0,
                                width: "0.2rem",
                                height: "0.2rem",
                                borderTop: `0.04rem solid ${colorMap[color]}`,
                                borderLeft: `0.04rem solid ${colorMap[color]}`,
                            } }), _jsx("div", { className: "absolute z-10 pointer-events-none", style: {
                                bottom: 0,
                                right: 0,
                                width: "0.2rem",
                                height: "0.2rem",
                                borderBottom: `0.04rem solid ${colorMap[color]}`,
                                borderRight: `0.04rem solid ${colorMap[color]}`,
                            } }), _jsx("div", { className: "absolute top-[0.04rem]  right-[0.04rem] flex items-center justify-between z-20", children: _jsx("button", { className: "text-white/80 hover:text-white text-[0.16rem] bg-black/30 px-[0.04rem] py-[0.04rem]  backdrop-blur-[0.04rem] hover:bg-black/50 transition-colors", onClick: (e) => toggleFullscreen(e, deviceCode), style: { fontSize: "0.16rem " }, children: isActive && isFullscreen ? "退出全屏" : "全屏" }) }), _jsxs("div", { className: `
                  flex-1 
                  bg-[#091E3F] 
                  relative 
                  overflow-hidden
                  ${status === "offline" ? "opacity-50" : ""}`, children: [_jsx("iframe", { src: status !== "offline" ? url : "", className: "absolute inset-0 w-full h-full border-0", title: `监控-${deviceShortName}`, allowFullScreen: true }), _jsxs("div", { className: "absolute bottom-[0.04rem] left-[0.04rem] right-[0.04rem] flex items-center justify-between z-20", children: [_jsx("div", { className: "flex items-center bg-black/30 px-[0.04rem] py-[0.04rem]  backdrop-blur-[0.04rem]", children: _jsx("span", { className: "text-white text-[0.16rem]", children: deviceShortName }) }), _jsxs("div", { className: "flex items-center bg-black/30 px-[0.04rem] py-[0.04rem]  backdrop-blur-[0.04rem]", children: [_jsx("span", { className: "inline-block w-[0.16rem] h-[0.16rem] rounded-full mr-[0.04rem] animate-pulse", style: { backgroundColor: colorMap[status] } }), _jsx("span", { className: "text-white text-[0.16rem]", children: status === "online"
                                                        ? "正常"
                                                        : status === "recording"
                                                            ? "录制中"
                                                            : "离线" })] })] })] })] }, deviceCode));
            }), isFullscreen && (_jsx("div", { className: "fixed inset-0 bg-black/80 z-50", onClick: () => {
                    setIsFullscreen(false);
                    setActiveMonitor(null);
                } })), _jsx("style", { children: `
        @keyframes scan {
          0% { top: 0%; }
          100% { top: 100%; }
        }
      ` })] }));
};
export default VideoSurveillance;
